import React from "react";
import { Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtCAD } from "@/lib/billing";

function Field({ label, children, hint, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
      {children}
      {hint ? <p className="text-[11px] text-slate-500 mt-1">{hint}</p> : null}
    </div>
  );
}

function Heading({ children, sub }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900">{children}</h3>
      {sub ? <p className="text-[11px] text-slate-500">{sub}</p> : null}
    </div>
  );
}

export function PlanFields({ form, setForm }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-3" data-testid="billing-plan-fields">
      <Heading sub="One monthly plan for every member. Changes apply from each member's next renewal.">Plan</Heading>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Field label="Price (CAD / month, before tax)">
          <Input type="number" min={0} step="0.01" value={form.plan_price_cad} onChange={set("plan_price_cad")} className="mt-1 min-h-[44px]" data-testid="billing-plan-price" />
        </Field>
        <Field label="Included enrichment credits">
          <Input type="number" min={0} value={form.included_enrichment} onChange={set("included_enrichment")} className="mt-1 min-h-[44px]" data-testid="billing-included-enrichment" />
        </Field>
        <Field label="Included scrape credits">
          <Input type="number" min={0} value={form.included_scrape} onChange={set("included_scrape")} className="mt-1 min-h-[44px]" data-testid="billing-included-scrape" />
        </Field>
        <Field label="Complimentary days at launch" hint="Existing members get this many free days from launch day.">
          <Input type="number" min={0} value={form.complimentary_days} onChange={set("complimentary_days")} className="mt-1 min-h-[44px]" data-testid="billing-complimentary-days" />
        </Field>
        <Field label="Swap rate (enrichment ⇄ scrape)" hint="Used by the admin credit-swap tool.">
          <Input type="number" min={0} step="0.01" value={form.swap_rate} onChange={set("swap_rate")} className="mt-1 min-h-[44px]" data-testid="billing-swap-rate" />
        </Field>
      </div>
    </div>
  );
}

export function FxAndMarkupFields({ form, setForm, settings, onRefresh, busy }) {
  const auto = !!form.usd_cad_rate_auto;
  const updated = settings.usd_cad_rate_updated_at ? new Date(settings.usd_cad_rate_updated_at).toLocaleString() : "never";
  const source = settings.usd_cad_rate_source === "Bank of Canada"
    ? `Bank of Canada${settings.usd_cad_rate_date ? ` · ${settings.usd_cad_rate_date}` : ""}`
    : settings.usd_cad_rate_source === "manual" ? "manual override" : "built-in default";
  return (
    <div className="space-y-3" data-testid="billing-fx-fields">
      <Heading sub="Top-up packs are priced from what the providers charge us (USD), converted to CAD, plus your markup.">Exchange rate &amp; markup</Heading>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
        <div className="rounded-md border border-slate-200 p-3 md:col-span-2" data-testid="billing-fx-block">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">USD → CAD</div>
              <div className="text-2xl font-bold text-slate-900 tabular-nums font-display" data-testid="billing-fx-rate">{Number(form.usd_cad_rate || 0).toFixed(4)}</div>
              <div className="text-[11px] text-slate-500" data-testid="billing-fx-meta">{source} · updated {updated}</div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <label className="flex items-center gap-2 text-xs" data-testid="billing-fx-auto-label">
                <Switch checked={auto} onCheckedChange={(v) => setForm({ ...form, usd_cad_rate_auto: v })} data-testid="billing-fx-auto" />
                <span>Auto-update daily (Bank of Canada)</span>
              </label>
              <Button size="sm" variant="outline" onClick={onRefresh} disabled={busy || !auto} data-testid="billing-fx-refresh">
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Refresh now
              </Button>
            </div>
          </div>
          {!auto ? (
            <Field label="Manual rate override" className="mt-3 max-w-[200px]" hint="Auto-update is paused while this is set.">
              <Input type="number" min={0.5} max={3} step="0.0001" value={form.usd_cad_rate} onChange={(e) => setForm({ ...form, usd_cad_rate: e.target.value })} className="mt-1 min-h-[44px]" data-testid="billing-usd-cad-rate" />
            </Field>
          ) : null}
        </div>
        <Field label="Markup on top-up credits (%)" hint="Pack price = provider cost in CAD × (1 + markup). Default 10 %.">
          <Input type="number" min={0} max={1000} step="0.5" value={form.topup_markup_pct} onChange={(e) => setForm({ ...form, topup_markup_pct: e.target.value })} className="mt-1 min-h-[44px]" data-testid="billing-markup-pct" />
        </Field>
      </div>
    </div>
  );
}

export const packPricing = (p, rate, markupPct, unitUsd) => {
  const cost = Number(p.credits || 0) * Number(unitUsd[p.pool] || 0) * rate;
  return { cost: Math.round(cost * 100) / 100, price: Math.round(cost * (1 + markupPct / 100) * 100) / 100 };
};

export function PacksEditor({ packs, onChange, rate, markupPct, unitUsd }) {
  const update = (i, patch) => onChange(packs.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const remove = (i) => onChange(packs.filter((_, idx) => idx !== i));
  const add = () => onChange([...packs, { id: `pack_${Date.now().toString(36)}`, pool: "enrichment", credits: 1000, label: "New pack" }]);
  return (
    <div className="space-y-3" data-testid="billing-packs-editor">
      <div className="flex items-center justify-between">
        <Heading sub="Top-up packs members can buy. Prices follow provider cost × exchange rate + markup; purchased credits never expire.">Credit packs</Heading>
        <Button size="sm" variant="outline" onClick={add} data-testid="billing-pack-add"><Plus className="h-3.5 w-3.5 mr-1" /> Add pack</Button>
      </div>
      <div className="space-y-2">
        {packs.map((p, i) => {
          const { cost, price } = packPricing(p, rate, markupPct, unitUsd);
          return (
            <div key={p.id} className="grid grid-cols-2 md:grid-cols-[1fr_140px_120px_1fr_auto] gap-2 items-end rounded-md border border-slate-200 p-2" data-testid={`billing-pack-row-${p.id}`}>
              <Field label="Label"><Input value={p.label} onChange={(e) => update(i, { label: e.target.value })} className="mt-1 min-h-[40px]" data-testid={`billing-pack-label-${p.id}`} /></Field>
              <Field label="Pool">
                <Select value={p.pool} onValueChange={(v) => update(i, { pool: v })}>
                  <SelectTrigger className="mt-1 min-h-[40px]" data-testid={`billing-pack-pool-${p.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="enrichment">Enrichment</SelectItem>
                    <SelectItem value="scrape">Scrape</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Credits"><Input type="number" min={1} value={p.credits} onChange={(e) => update(i, { credits: e.target.value })} className="mt-1 min-h-[40px]" data-testid={`billing-pack-credits-${p.id}`} /></Field>
              <div className="pb-1">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Price (auto)</div>
                <div className="text-sm font-semibold text-slate-900 tabular-nums" data-testid={`billing-pack-price-${p.id}`}>{fmtCAD(price)}</div>
                <div className="text-[11px] text-slate-500">cost {fmtCAD(cost)} · {p.credits > 0 ? `${fmtCAD((price / Number(p.credits)) * 1000)}/1k` : ""}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(i)} title="Remove pack" className="mb-1" data-testid={`billing-pack-remove-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          );
        })}
        {!packs.length ? <p className="text-xs text-slate-500">No packs — members won't be able to top up.</p> : null}
      </div>
    </div>
  );
}

export function TaxFields({ form, setForm, settings }) {
  const tax = form.tax;
  const setTax = (patch) => setForm({ ...form, tax: { ...tax, ...patch } });
  const sales = Number(settings.taxable_sales_12m_cad || 0);
  const threshold = Number(tax.threshold_cad || 30000);
  const pct = Math.min(100, Math.round((sales / Math.max(threshold, 1)) * 100));
  return (
    <div className="space-y-3" data-testid="billing-tax-fields">
      <Heading sub="Saskatchewan PST always applies to SK customers; GST/HST switches on at the small-supplier threshold or when you register.">Tax</Heading>
      <pre className="whitespace-pre-wrap text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3 font-sans" data-testid="billing-tax-rules">{settings.tax_rules}</pre>
      <div className="rounded-md border border-slate-200 p-3">
        <div className="flex items-center justify-between text-xs text-slate-600">
          <span>Taxable sales · trailing 12 months</span>
          <span className="font-medium text-slate-900 tabular-nums" data-testid="billing-sales-12m">{fmtCAD(sales)} / {fmtCAD(threshold)}</span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden">
          <div className={pct >= 80 ? "h-full bg-amber-500" : "h-full bg-emerald-500"} style={{ width: `${pct}%` }} />
        </div>
        <p className={"text-xs mt-2 " + (settings.gst_active ? "text-amber-700 font-medium" : "text-slate-500")} data-testid="billing-gst-status">
          {settings.gst_active ? "GST/HST is being charged" : "GST/HST not charged yet"} — {settings.gst_reason}
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
        <label className="flex items-center gap-2 text-sm min-h-[44px]" data-testid="billing-gst-registered-label">
          <Switch checked={!!tax.gst_registered} onCheckedChange={(v) => setTax({ gst_registered: v })} data-testid="billing-gst-registered" />
          <span>GST/HST registered</span>
        </label>
        <Field label="Registered from">
          <Input type="date" value={(tax.gst_registered_from || "").slice(0, 10)} onChange={(e) => setTax({ gst_registered_from: e.target.value })} className="mt-1 min-h-[44px]" data-testid="billing-gst-from" />
        </Field>
        <Field label="Threshold (CAD)">
          <Input type="number" min={0} value={tax.threshold_cad} onChange={(e) => setTax({ threshold_cad: e.target.value })} className="mt-1 min-h-[44px]" data-testid="billing-gst-threshold" />
        </Field>
        <Field label="SK PST rate">
          <Input type="number" min={0} max={1} step="0.01" value={tax.pst_sk_rate} onChange={(e) => setTax({ pst_sk_rate: e.target.value })} className="mt-1 min-h-[44px]" data-testid="billing-pst-rate" />
        </Field>
      </div>
    </div>
  );
}

export function ReceiptFields({ receipt, onChange }) {
  const set = (k) => (e) => onChange({ ...receipt, [k]: e.target.value });
  return (
    <div className="space-y-3" data-testid="billing-receipt-fields">
      <Heading sub="Printed on every PDF receipt.">Receipt details</Heading>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Legal business name"><Input value={receipt.legal_name || ""} onChange={set("legal_name")} className="mt-1 min-h-[44px]" data-testid="billing-receipt-legal-name" /></Field>
        <Field label="Support email"><Input type="email" value={receipt.support_email || ""} onChange={set("support_email")} className="mt-1 min-h-[44px]" data-testid="billing-receipt-support-email" /></Field>
        <Field label="Business address" className="sm:col-span-2"><Input value={receipt.address || ""} onChange={set("address")} className="mt-1 min-h-[44px]" data-testid="billing-receipt-address" /></Field>
        <Field label="PST number (SK)"><Input value={receipt.pst_number || ""} onChange={set("pst_number")} className="mt-1 min-h-[44px]" data-testid="billing-receipt-pst" /></Field>
        <Field label="GST/HST number"><Input value={receipt.gst_hst_number || ""} onChange={set("gst_hst_number")} className="mt-1 min-h-[44px]" data-testid="billing-receipt-gst" /></Field>
        <Field label="Terms / footer note" className="sm:col-span-2">
          <Textarea value={receipt.terms || ""} onChange={set("terms")} rows={3} className="mt-1" data-testid="billing-receipt-terms" />
        </Field>
      </div>
    </div>
  );
}
